import { useCallback, useEffect, useState } from 'react';
import { happyHourService, type HappyHourView } from '../../../services/happy-hour/happy-hour.service';

type UseHappyHourControllerParams = {
  tenantId: string;
  employeeSession: string | null;
};

export function useHappyHourController({
  tenantId,
  employeeSession,
}: UseHappyHourControllerParams) {
  const [happyHour, setHappyHour] = useState<HappyHourView | null>(null);
  const [isHappyHourSetupOpen, setIsHappyHourSetupOpen] = useState(false);
  const [isHappyHourEditing, setIsHappyHourEditing] = useState(false);
  const [isHappyHourActiveOpen, setIsHappyHourActiveOpen] = useState(false);

  useEffect(() => {
    if (!employeeSession) {
      setHappyHour(null);
      return;
    }

    let cancelled = false;
    void happyHourService.get(tenantId, employeeSession)
      .then((nextHappyHour) => {
        if (!cancelled) {
          setHappyHour(nextHappyHour);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHappyHour(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [employeeSession, tenantId]);

  const openHappyHour = useCallback(() => {
    if (happyHour?.active) {
      setIsHappyHourActiveOpen(true);
      return;
    }
    setIsHappyHourEditing(false);
    setIsHappyHourSetupOpen(true);
  }, [happyHour?.active]);

  return {
    happyHour,
    setHappyHour,
    isHappyHourSetupOpen,
    isHappyHourActiveOpen,
    isHappyHourEditing,
    openHappyHour,
    // Edit path: the active modal hands off to the setup wizard, pre-filled from the live config.
    openHappyHourSetup: () => {
      setIsHappyHourEditing(true);
      setIsHappyHourSetupOpen(true);
    },
    closeHappyHourSetup: () => setIsHappyHourSetupOpen(false),
    closeHappyHourActive: () => setIsHappyHourActiveOpen(false),
  };
}
